import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Container, Grid } from '../../components/mui/Core/index'
import GenericNav from '../../components/genericcomponents/navbars/GenericNav'
import useBrowserTabTitles from '../../hooks/browserTabTitles/browser-tab-titles'
import ErrorState from '../../components/ErrorState/error-state'
import { errorCodes } from '../../constants/error-codes'
import { fetchClients } from '../userauth/login/static-data-slice'
import { setSnackbar } from '../../components/GenericSnackbar/generic-snackbar-slice'
import { userPermissionConstants } from '../../constants/user-permission-constants'
import { hasUserPermission } from '../../utils/user-utils'
import SelectClientDropdown from '../../components/dropdowns/SelectClientDropdown/select-client-dropdown'
import ClientSelection from '../../components/ClientSelection/client-selection'
import * as Constants from '../../constants/en.json'
import { sessionStorageKeyConstants } from '../../constants/session-storage-key-constants'
import TableauDashboard from './components/TableauDashboard/tableau-dashboard'
import { localStorageKeyConstants } from '../../constants/local-storage-key-constants'
import { GenericSnackbar } from '../../components/GenericSnackbar/generic-snackbar'
import { getTableauCredentials } from '../../api/reporting/tableau/tableau-get-credentials'

function Dashboard() {
  const dispatch = useDispatch()
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const fetchClientsError = useSelector((state) => state.staticData.fetchClientsError)
  const clients = useSelector((state) => state.staticData.clients)
  const [tableauToken, setTableauToken] = useState(
    sessionStorage.getItem(sessionStorageKeyConstants.TABLEAU_TOKEN),
  )
  const [selectedClient, setSelectedClient] = useState(
    JSON.parse(sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT)),
  )
  const hasSwitchClientPermission = hasUserPermission({
    permissionCode: userPermissionConstants.SWITCH_CLIENT,
    permissions: authenticatedUser?.permissions,
  })
  const [tableauDashboardUrl, setTableauDashboardUrl] = useState('')
  const browserTabTitle = selectedClient?.clientShortName
    ? `${selectedClient?.clientShortName} - ${Constants.dashboard.HOME}`
    : Constants.dashboard.HOME
  useBrowserTabTitles(browserTabTitle)
  const [loading, setIsLoading] = useState(true)

  const clientSelectContainerStyle = selectedClient
    ? { marginTop: '1px', marginLeft: '124px' }
    : { marginTop: '1px' }

  useEffect(() => {
    setIsLoading(true)
    if (hasSwitchClientPermission && clients?.length === 0) {
      dispatch(fetchClients())
    }
    getTableauCredentials(authenticatedUser?.email)
      .then((response) => {
        setIsLoading(false)
        const token = response?.data?.data?.token
        const wmsDashboardURL = response?.data?.data?.dashboardURLs?.wmsDashboardURL
        const dmsDashboardURL = response?.data?.data?.dashboardURLs?.dmsDashboardURL
        const stateDashboardURL = response?.data?.data?.dashboardURLs?.stateDashboardURL

        sessionStorage.setItem(sessionStorageKeyConstants.TABLEAU_TOKEN, token)
        localStorage.setItem(localStorageKeyConstants.WMS_TABLEAU_DASHBOARD_URL, wmsDashboardURL)
        localStorage.setItem(localStorageKeyConstants.DMS_TABLEAU_DASHBOARD_URL, dmsDashboardURL)
        localStorage.setItem(
          localStorageKeyConstants.STATE_TABLEAU_DASHBOARD_URL,
          stateDashboardURL,
        )

        setTableauToken(token)
      })
      .catch((exception) => {
        setIsLoading(false)
        setSnackbar({ message: exception?.message, severity: 'error', action: true })
      })
      .finally(() => setIsLoading(false))
  }, [authenticatedUser?.email, dispatch, hasSwitchClientPermission, clients?.length])

  useEffect(() => {
    if (fetchClientsError) {
      dispatch(
        setSnackbar({ message: fetchClientsError?.message, severity: 'error', action: true }),
      )
    }
  }, [dispatch, fetchClientsError])

  useEffect(() => {
    const dmsUser = hasSwitchClientPermission ? selectedClient?.dmsFlag : authenticatedUser?.dms
    const clientCode = hasSwitchClientPermission
      ? selectedClient?.clientCode
      : authenticatedUser?.clientCode
    const url =
      // eslint-disable-next-line no-nested-ternary
      dmsUser === 'Y'
        ? localStorage.getItem(localStorageKeyConstants.DMS_TABLEAU_DASHBOARD_URL)
        : clientCode === 'DEFAULT'
          ? localStorage.getItem(localStorageKeyConstants.STATE_TABLEAU_DASHBOARD_URL)
          : localStorage.getItem(localStorageKeyConstants.WMS_TABLEAU_DASHBOARD_URL)
    if (url !== tableauDashboardUrl) {
      setTableauDashboardUrl(url)
    }
  }, [
    tableauToken,
    authenticatedUser?.dms,
    hasSwitchClientPermission,
    selectedClient?.dmsFlag,
    selectedClient?.clientCode,
    tableauDashboardUrl,
    authenticatedUser?.clientCode,
  ])

  const handleSelectClientDropdown = (client) => {
    setSelectedClient(client)
  }

  if (!loading && !tableauToken) {
    return <ErrorState errorCode={errorCodes.http.TABLEAU_ERROR_404} />
  }

  const tableauContainerStyle = {
    paddingTop: '20px',
    marginLeft: '121px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  }

  return (
    <Box data-testid='dashboard'>
      <GenericNav selected={Constants.dashboard.HOME} user='authenticated' />
      <Container maxWidth='xl'>
        <Grid container>
          {hasSwitchClientPermission && !selectedClient && (
            <Box sx={clientSelectContainerStyle}>
              <ClientSelection
                handleSelectOption={handleSelectClientDropdown}
                message={Constants.dashboard.SELECT_CLIENT_MESSAGE}
              />
            </Box>
          )}
          {hasSwitchClientPermission && selectedClient && (
            <>
              <Box sx={clientSelectContainerStyle}>
                <Grid size={{ xs: 8 }}>
                  <SelectClientDropdown
                    currentState={selectedClient}
                    handleSelectOption={handleSelectClientDropdown}
                  />
                </Grid>
              </Box>
              {!loading && (
                <Box sx={tableauContainerStyle}>
                  <TableauDashboard
                    tableauDashboardURL={tableauDashboardUrl}
                    tableauToken={tableauToken}
                    clientCode={selectedClient?.clientCode}
                  />
                </Box>
              )}
            </>
          )}
          {!loading && !hasSwitchClientPermission && (
            <Box sx={tableauContainerStyle}>
              <TableauDashboard
                tableauDashboardURL={tableauDashboardUrl}
                tableauToken={tableauToken}
                clientCode={authenticatedUser?.clientCode}
              />
            </Box>
          )}
        </Grid>
      </Container>
      <GenericSnackbar />
    </Box>
  )
}

export default Dashboard
